import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BehaviorSubject, ReplaySubject, Subscription, filter, mergeMap } from 'rxjs';
import {
  ConfirmData,
  ConfirmationDialogComponent,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { IntegrationModel } from 'src/app/models/api';
import { Platform } from 'src/app/models/api/common';
import { AccountSettingsDomainsModel } from 'src/app/models/api/governance/account-settings.model';
import { EnvironmentModel } from 'src/app/models/api/governance/environment.model';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { NavigationParams } from 'src/app/models/navigation/navigationParams.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { PolicyTypeSelectOptions } from 'src/app/models/selectOptions/policyTypeSelectOptions.model';
import { AccountSettingsHttpService } from 'src/app/services/httpServices/governance/accountSettingsHttpService/account-settings-http.service';
import { EnvironmentsHttpService } from 'src/app/services/httpServices/governance/environmentsHttpService/environments-http.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { QueryParamsService } from 'src/app/services/queryParams/query-params.service';

@Component({
  selector: 'app-policy-config',
  templateUrl: './policy-config.component.html',
  styleUrls: ['./policy-config.component.scss'],
})
export class PolicyConfigComponent implements OnInit, OnDestroy {
  @Input() public environments = signal<EnvironmentModel[]>([]);

  public policyModel: PolicyModel;
  public integrationModel: IntegrationModel;
  public policyTypeSelected?: ISelectOption;
  public domains: string[];

  public platformsWithoutConnectors = [
    Platform.powerBI,
    Platform.salesforce,
    Platform.chatGptEnterprise,
    Platform.amazonBedrock,
    Platform.google,
    Platform.devices,
    Platform.cursor,
  ];
  public platformsWithoutEnvironments = [
    Platform.powerBI,
    Platform.salesforce,
    Platform.chatGptEnterprise,
    Platform.amazonBedrock,
    Platform.google,
    Platform.devices,
    Platform.cursor,
  ];

  // tabs
  public tabs: ISidepanelTab[] = [];
  public currentTabIndex = 0;
  public changeCurrentTabIndex: ReplaySubject<number> = new ReplaySubject<number>();

  // loading behavior subject
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // query fields
  public policyId: string | null;
  public integrationId: string;

  // subscriptions
  private _queryParamsSubscription: Subscription;
  private _integrationStoreSubscription: Subscription;

  // confirmation dialog content
  private _firstTime = true;
  private readonly _title = 'Are you sure?';
  private readonly _content = 'You have unsaved changes that will be lost, proceed anyway?';
  private _changesWereDone = false;

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _activatedRoute: ActivatedRoute,
    public _matDialog: MatDialog,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _environmentsHttpService: EnvironmentsHttpService,
    private readonly _accountSettingsHttpService: AccountSettingsHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _queryParamsService: QueryParamsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _featureFlagsService: FeatureFlagsService
  ) {}

  ngOnInit(): void {
    this._getDomains();
    this._handleUrlQueryParamsOnChange();
  }

  ngOnDestroy() {
    this._queryParamsSubscription.unsubscribe();
    this._integrationStoreSubscription.unsubscribe();
  }

  public changesWereDoneUpdate(value: boolean) {
    this._changesWereDone = value;
  }

  public updateEnvironments(envs: EnvironmentModel[]) {
    this.environments.set(envs);
  }

  public onTabSelect(selectedIndex: number) {
    if (this._firstTime) {
      this._firstTime = false;
      this.currentTabIndex = selectedIndex;
    } else if (this._changesWereDone && this.currentTabIndex !== selectedIndex) {
      // } else if (this.content) {
      const confirmationData: ConfirmData = {
        title: this._title,
        content: this._content,
        icon: 'triangle-alert',
      };
      const approveUpdatesDialogRef = this._matDialog.open(ConfirmationDialogComponent, {
        data: confirmationData,
        disableClose: false,
        panelClass: 'confirmation-dialog-container',
      });
      approveUpdatesDialogRef.afterClosed().subscribe((userConfirmed) => {
        if (userConfirmed) {
          this.currentTabIndex = selectedIndex;
          this._changesWereDone = false;
        } else {
          this.changeCurrentTabIndex.next(this.currentTabIndex);
        }
      });
    } else {
      this.currentTabIndex = selectedIndex;
    }
  }

  public canEdit(): boolean {
    return this._permissionsService.userHasPermission(PERMISSIONS.POLICY_OPERATION, this.integrationId);
  }

  public backToPolicies() {
    this._router.navigate(['app/policies'], {
      queryParams: { integration: this.policyModel.integrationId },
    });
  }

  public updatePolicy(policy: PolicyModel) {
    this.policyModel = policy;
  }

  private _handleUrlQueryParamsOnChange() {
    this.isLoading$.next(true);
    this._queryParamsSubscription = this._queryParamsService.toNavigate$.subscribe({
      next: (navigationParams) => this._onNavigationParamsUpdated(navigationParams),
      error: (error) => this._httpErrorHandlerService.handle(error),
    });
  }

  private _onNavigationParamsUpdated(navigationParams: NavigationParams) {
    if (navigationParams.integration == null) {
      return;
    }

    this.integrationId = navigationParams.integration;

    this._integrationStoreSubscription = this._store.integrations$
      .pipe(
        mergeMap((integrations) => integrations),
        filter((integration) => integration.id === this.integrationId)
      )
      .subscribe((integrationModel) => {
        this.integrationModel = integrationModel;
      });

    this._setEnvironments();

    this._activatedRoute.paramMap.subscribe((params: ParamMap) => {
      this.policyId = params.get('id');
      this._getPolicyModel();
    });
  }

  private _setEnvironments() {
    if (this.environments().length === 0)
      this._environmentsHttpService
        .getEnvironments(this.integrationId)
        .subscribe((environmentsModel: EnvironmentModel[]) => {
          this.environments.set(environmentsModel);
        });
  }

  private _getPolicyModel() {
    if (this.policyId == null || this.integrationId == null) {
      return;
    }

    this._policiesHttpService.getPolicy(this.integrationId, this.policyId).subscribe({
      next: (response: PolicyModel) => {
        this.policyModel = response;
        this.policyTypeSelected = PolicyTypeSelectOptions.find((type) => type.value === this.policyModel.type);

        this._setUpConfigurationTabs();

        this.isLoading$.next(false);
      },
      error: (error) => this._httpErrorHandlerService.handle(error),
    });
  }

  private _getDomains() {
    this._accountSettingsHttpService.getDomains().subscribe((domainsModel: AccountSettingsDomainsModel) => {
      this.domains = domainsModel.domains;
    });
  }

  private _setUpConfigurationTabs() {
    const tabs = [
      { labelPath: 'policyPage.config.generalTab' },
      { labelPath: 'policyPage.config.trustedDomainsTab' },
      { labelPath: 'policyPage.config.endpointFilteringTab' },
    ];

    if (
      this._featureFlagsService.isFeatureEnabled('policy_sensitive_data') &&
      this._isPowerPlatform() &&
      this.policyModel?.isDefault
    ) {
      tabs.push({ labelPath: 'policyPage.config.sensitiveLabelsTab' });
      tabs.push({ labelPath: 'policyPage.config.sensitiveLocationsTab' });
    }

    if (!this.platformsWithoutEnvironments.includes(this.integrationModel.platform)) {
      tabs.push({ labelPath: 'policyPage.config.environmentsTab' });
    }

    if (!this.platformsWithoutConnectors.includes(this.integrationModel.platform)) {
      tabs.push({ labelPath: 'policyPage.config.connectorsTab' });
    }

    // Add MCP Server Filtering tab for devices and cursor platforms
    if (this.integrationModel.platform === Platform.devices || this.integrationModel.platform === Platform.cursor) {
      tabs.push({ labelPath: 'policyPage.config.mcpServerFilteringTab' });
    }

    this.tabs = tabs;
  }

  private _isPowerPlatform(): boolean {
    return this.integrationModel.platform === Platform.powerplatform;
  }

  // Dynamic tab index getters
  public get environmentsTabIndex(): number {
    return this.tabs.findIndex((tab) => tab.labelPath === 'policyPage.config.environmentsTab');
  }

  public get connectorsTabIndex(): number {
    return this.tabs.findIndex((tab) => tab.labelPath === 'policyPage.config.connectorsTab');
  }

  public get mcpServerFilteringTabIndex(): number {
    return this.tabs.findIndex((tab) => tab.labelPath === 'policyPage.config.mcpServerFilteringTab');
  }

  public get sensitiveLabelsTabIndex(): number {
    return this.tabs.findIndex((tab) => tab.labelPath === 'policyPage.config.sensitiveLabelsTab');
  }

  public get sensitiveLocationsTabIndex(): number {
    return this.tabs.findIndex((tab) => tab.labelPath === 'policyPage.config.sensitiveLocationsTab');
  }
}
